/** Shared list-filter helpers for polygon portfolio tabs (Příprava / Stavba / Archiv / …). */

import type { ParamMap, Params } from '@angular/router';
import {
  isPolygonPlanStatusTabId,
  POLYGON_PLAN_STATUS_TAB_FILTER_OPTIONS,
  polygonPlanStatusTabId,
  type PolygonPlanStatusTabId,
} from '@core/shared-bridge';
import type { Polygon } from '../../core/models/polygon.model';
import { DEFAULT_SHOW_ACTIVE_POLYGONS_ONLY } from './active-polygon-filter.util';
import { parseFilterCsvFromQuery, setQueryCsv, setQueryString } from './list-query-params.util';
import { polygonProjectStateLabel } from './polygon-project-tracking.util';

export const POLYGON_PLAN_STATUS_TAB_QUERY_KEY = 'planStatus';

/** Query value for “every portfolio tab” when the default is a non-empty subset. */
export const POLYGON_PLAN_STATUS_FILTER_ALL = 'all';

/**
 * Former „Ukázat pouze aktivní“ (API `planState=aktivni`): projekce, příprava, stavba.
 * Nezařazené and archiv stay out until the user picks them.
 */
export const DEFAULT_ACTIVE_POLYGON_PLAN_TABS: readonly PolygonPlanStatusTabId[] = [
  'projekce',
  'priprava',
  'stavba',
];

export const POLYGON_PLAN_STATUS_TAB_FILTER_UI_OPTIONS: { value: string; label: string }[] =
  POLYGON_PLAN_STATUS_TAB_FILTER_OPTIONS.map((o) => ({ value: o.value, label: o.label }));

/** Legacy mezistav slugs (`plan=`) → portfolio tab. */
const LEGACY_PLAN_SLUG_TO_TAB: Record<string, PolygonPlanStatusTabId> = {
  neurceno: 'nezarazene',
  neprirazeno: 'nezarazene',
  nezarazene: 'nezarazene',
  projekce: 'projekce',
  inzenyring: 'priprava',
  priprava_zahajeni: 'priprava',
  priprava_staveb: 'priprava',
  priprava: 'priprava',
  bremena: 'projekce',
  stavba: 'stavba',
  reklamace: 'stavba',
  uzavrena: 'archiv',
  zrusena: 'archiv',
  archiv: 'archiv',
};

export function polygonPlanStatusTabForPolygon(polygon: Polygon): PolygonPlanStatusTabId {
  return polygonPlanStatusTabId(polygonProjectStateLabel(polygon));
}

export function polygonMatchesPlanStatusTabFilter(
  polygon: Polygon,
  tabs: readonly PolygonPlanStatusTabId[],
): boolean {
  if (!tabs.length) return true;
  return tabs.includes(polygonPlanStatusTabForPolygon(polygon));
}

export function filterPolygonsByPlanStatusTabs(
  polygons: readonly Polygon[],
  tabs: readonly PolygonPlanStatusTabId[],
): Polygon[] {
  if (!tabs.length) return [...polygons];
  return polygons.filter((p) => polygonMatchesPlanStatusTabFilter(p, tabs));
}

export function buildPolygonPlanStatusTabById(
  polygons: readonly Polygon[],
): Map<string, PolygonPlanStatusTabId> {
  const map = new Map<string, PolygonPlanStatusTabId>();
  for (const polygon of polygons) {
    const id = (polygon.id ?? '').trim();
    if (!id) continue;
    map.set(id, polygonPlanStatusTabForPolygon(polygon));
  }
  return map;
}

/** `null` = no tab restriction. */
export function polygonIdSetForPlanStatusTabs(
  polygons: readonly Polygon[],
  tabs: readonly PolygonPlanStatusTabId[],
): Set<string> | null {
  if (!tabs.length) return null;
  const allowed = new Set(tabs);
  const ids = new Set<string>();
  for (const polygon of polygons) {
    const id = (polygon.id ?? '').trim();
    if (!id) continue;
    if (allowed.has(polygonPlanStatusTabForPolygon(polygon))) ids.add(id);
  }
  return ids;
}

export function parsePolygonPlanStatusTabsFromQuery(
  pm: Pick<ParamMap, 'get'>,
  queryKey = POLYGON_PLAN_STATUS_TAB_QUERY_KEY,
): PolygonPlanStatusTabId[] {
  const fromCanonical = parseFilterCsvFromQuery(pm.get(queryKey)).filter(isPolygonPlanStatusTabId);
  if (fromCanonical.length > 0) return fromCanonical;
  if (queryKey === POLYGON_PLAN_STATUS_TAB_QUERY_KEY) {
    const legacy = parseFilterCsvFromQuery(pm.get('plan'))
      .map((v) => LEGACY_PLAN_SLUG_TO_TAB[v.toLowerCase()])
      .filter((v): v is PolygonPlanStatusTabId => !!v);
    if (legacy.length > 0) return [...new Set(legacy)];
  }
  return [];
}

export function setPolygonPlanStatusTabsQuery(
  p: Params,
  tabs: readonly PolygonPlanStatusTabId[],
  queryKey = POLYGON_PLAN_STATUS_TAB_QUERY_KEY,
): void {
  setQueryCsv(p, queryKey, [...tabs]);
}

export function isDefaultActivePolygonPlanTabs(tabs: readonly string[]): boolean {
  if (tabs.length !== DEFAULT_ACTIVE_POLYGON_PLAN_TABS.length) return false;
  const selected = new Set(tabs);
  return DEFAULT_ACTIVE_POLYGON_PLAN_TABS.every((tab) => selected.has(tab));
}

/**
 * Objects list (and any screen that replaced the active-only checkbox).
 * Explicit `planStatus` wins. `planStatus=all` is every status.
 * Missing plan filter + legacy `active=0` is every status; otherwise the checked default.
 */
export function parseActivePolygonPlanTabsFromQuery(
  pm: Pick<ParamMap, 'get'>,
): PolygonPlanStatusTabId[] {
  const planStatusRaw = (pm.get(POLYGON_PLAN_STATUS_TAB_QUERY_KEY) ?? '').trim().toLowerCase();
  const planRaw = (pm.get('plan') ?? '').trim().toLowerCase();
  if (
    planStatusRaw === POLYGON_PLAN_STATUS_FILTER_ALL ||
    (!planStatusRaw && planRaw === POLYGON_PLAN_STATUS_FILTER_ALL)
  ) {
    return [];
  }
  const parsed = parsePolygonPlanStatusTabsFromQuery(pm);
  if (parsed.length > 0) return parsed;
  if (!legacyShowActivePolygonsOnly(pm)) return [];
  return [...DEFAULT_ACTIVE_POLYGON_PLAN_TABS];
}

/** Legacy `active=0|1` from the removed checkbox. Missing key keeps the old default (on). */
function legacyShowActivePolygonsOnly(
  pm: Pick<ParamMap, 'get'>,
  defaultValue = DEFAULT_SHOW_ACTIVE_POLYGONS_ONLY,
): boolean {
  const raw = (pm.get('active') ?? '').trim().toLowerCase();
  if (raw === '0' || raw === 'false') return false;
  if (raw === '1' || raw === 'true') return true;
  return defaultValue;
}

/** Default subset is omitted. Empty selection is `planStatus=all` so it does not snap back. */
export function setActivePolygonPlanTabsQuery(
  p: Params,
  tabs: readonly PolygonPlanStatusTabId[],
): void {
  if (tabs.length === 0) {
    setQueryString(p, POLYGON_PLAN_STATUS_TAB_QUERY_KEY, POLYGON_PLAN_STATUS_FILTER_ALL);
    return;
  }
  if (isDefaultActivePolygonPlanTabs(tabs)) return;
  setPolygonPlanStatusTabsQuery(p, tabs);
}

export function sanitizePolygonIdsForPlanStatusTabs(
  selectedIds: readonly string[],
  polygons: readonly Polygon[],
  tabs: readonly PolygonPlanStatusTabId[],
): string[] {
  if (!selectedIds.length || !tabs.length) return [...selectedIds];
  const allowed = polygonIdSetForPlanStatusTabs(polygons, tabs);
  if (!allowed) return [...selectedIds];
  return selectedIds.filter((id) => allowed.has(id));
}
